import React, { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Loader2, Tag, Gift, X } from "lucide-react";
// Relative: the "@" alias does not exist under Jest, and these must resolve for real in
// every suite that mounts the signup form or the Billing page.
import { PLATFORM_OFFERS as IDS } from "../../constants/testIds/platformOffers";
import useSaasCodePreview from "../../platformOffers/useSaasCodePreview";
import { platformOfferErrorMessage } from "../../platformOffers/errors";
import { offerSummary, perInvoiceText } from "../../platformOffers/describe";

const ALL_KINDS = ["promo", "giftcard"];

// The two planes the field lives on: the dark signup page and the cream admin console.
// A native <input> rather than the shadcn one: the latter imports "@/lib/utils", which the
// Billing page's suites do not shim, and a code field is one plain text input.
const TONES = {
  dark: {
    label: "text-white/70",
    input: "bg-white/5 border-white/10 text-white placeholder:text-white/30",
    apply: "bg-sol-gold hover:bg-sol-gold/90 text-black",
    result: "bg-emerald-500/10 border-emerald-400/30 text-white",
    muted: "text-white/60",
    error: "text-red-400",
  },
  light: {
    label: "text-eh-ink",
    input: "bg-white border-eh-border-soft text-eh-ink placeholder:text-eh-muted",
    // Its own hover, like the dark tone's: the shared Button's default
    // `hover:bg-primary/90` paints the practice's pale primary behind light text
    // (measured 1.29:1 on hover).
    apply: "bg-eh-ink hover:bg-eh-ink/90 text-eh-bg",
    result: "bg-emerald-50 border-emerald-200 text-eh-ink",
    muted: "text-eh-muted",
    error: "text-rose-600",
  },
};

const LABEL_KEYS = {
  any: "platformOffers.code.labelAny",
  promo: "platformOffers.code.labelPromo",
  giftcard: "platformOffers.code.labelGiftcard",
};

/**
 * Entry of one platform promo code or gift card, previewed against a plan (contract §5.1).
 *
 * CONTROLLED. `value` is the applied preview (or null) and belongs to the caller, because
 * the caller is the one that sends it (signup body, change-plan body, redeem call) and
 * shows its consequences (the signup summary). This field previews on an explicit Apply,
 * hands the preview up through `onApplied`, and renders `value` when there is one.
 *
 * `allowedKinds` narrows what the field accepts. The server decides the kind from the
 * code; a preview of a kind this field does not take is refused HERE with
 * `refusedKindText[kind]` (or a generic sentence), since the preview endpoint itself
 * happily previews both.
 *
 * @param {string} scope            testid scope: signup-promo | signup-giftcard | plan-change | billing-redeem
 * @param {?object} value           the applied §5.1 preview, or null
 * @param {string} [initialCode]    pre-filled text (the `/platform/signup?code=` deep link)
 * @param {{plan_id, currency, billing_cycle}} params  what the preview is priced against
 * @param {string[]} [allowedKinds] subset of ["promo", "giftcard"]
 * @param {{promo?: string, giftcard?: string}} [refusedKindText] resolved sentences
 * @param {boolean} [disabled]
 * @param {(preview) => void} onApplied
 * @param {() => void} [onRemoved] called by Remove; needed whenever `value` can be non-null
 * @param {"dark" | "light"} [tone]
 */
export default function CodeField({
  scope,
  value,
  initialCode = "",
  params,
  allowedKinds = ALL_KINDS,
  refusedKindText = {},
  disabled = false,
  onApplied,
  onRemoved,
  tone = "light",
}) {
  const tc = useT("common");
  const { lang } = useLanguage();
  const inputId = useId();
  const skin = TONES[tone];
  const [text, setText] = useState(initialCode);
  const [refusedKind, setRefusedKind] = useState(null);
  const { status, error, apply, clear } = useSaasCodePreview();

  const code = text.trim();
  const loading = status === "loading";
  const canApply = Boolean(code) && Boolean(params?.plan_id) && !loading && !disabled;

  const submit = async () => {
    if (!canApply) return;
    setRefusedKind(null);
    const preview = await apply(code, params);
    if (!preview) return;
    // The preview now lives with the caller (or is refused): this field goes back to idle.
    clear();
    if (!allowedKinds.includes(preview.kind)) {
      setRefusedKind(preview.kind);
      return;
    }
    setText("");
    onApplied(preview);
  };

  const remove = () => {
    setRefusedKind(null);
    onRemoved();
  };

  if (value) {
    const Icon = value.kind === "giftcard" ? Gift : Tag;
    return (
      <div data-testid={IDS.codeField(scope)}>
        <div
          data-testid={IDS.codeResult(scope)}
          data-kind={value.kind}
          className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${skin.result}`}
        >
          <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div>
              <code className="font-semibold tracking-wide">{value.code}</code>
              {" — "}
              {offerSummary(value, tc, lang)}
            </div>
            {value.kind === "promo" && (
              <div className={`mt-0.5 text-xs ${skin.muted}`}>{perInvoiceText(value, tc, lang)}</div>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={remove}
              data-testid={IDS.codeRemove(scope)}
              aria-label={tc("platformOffers.code.remove")}
              className={`inline-flex items-center gap-1 text-xs ${skin.muted} hover:underline`}
            >
              <X className="w-3.5 h-3.5" /> {tc("platformOffers.code.remove")}
            </button>
          )}
        </div>
      </div>
    );
  }

  const errorText = refusedKind
    ? refusedKindText[refusedKind] || tc("platformOffers.code.kindRefused")
    : status === "error"
      ? platformOfferErrorMessage(error, tc, tc("platformOffers.error.generic"))
      : null;

  return (
    <div data-testid={IDS.codeField(scope)}>
      <label htmlFor={inputId} className={`mb-2 block text-sm ${skin.label}`}>
        {tc(allowedKinds.length === 1 ? LABEL_KEYS[allowedKinds[0]] : LABEL_KEYS.any)}
      </label>
      <div className="flex gap-2">
        <input
          id={inputId}
          data-testid={IDS.codeInput(scope)}
          value={text}
          onChange={(e) => { setText(e.target.value); setRefusedKind(null); }}
          onKeyDown={(e) => {
            // Inside the signup <form>, Enter would submit the whole signup: here it
            // means Apply.
            if (e.key === "Enter") { e.preventDefault(); submit(); }
          }}
          disabled={disabled}
          maxLength={64}
          autoComplete="off"
          spellCheck={false}
          placeholder={tc("platformOffers.code.placeholder")}
          aria-invalid={Boolean(errorText)}
          className={`flex-1 min-w-0 h-11 rounded-xl border px-3 text-sm uppercase placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-amber-300/40 disabled:opacity-60 ${skin.input}`}
        />
        <Button
          type="button"
          onClick={submit}
          disabled={!canApply}
          data-testid={IDS.codeApply(scope)}
          className={`h-11 rounded-xl px-5 disabled:opacity-50 ${skin.apply}`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : tc("platformOffers.code.apply")}
        </Button>
      </div>
      {errorText && (
        <p role="alert" data-testid={IDS.codeError(scope)} className={`mt-2 text-xs ${skin.error}`}>
          {errorText}
        </p>
      )}
    </div>
  );
}
